import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import Dialog from '../components/Dialog';
import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';

import useDiaryData from '../hooks/useDiaryData';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useMainContext from '../hooks/useMainContext';

import todoes from '../api/todoes';
import { maxWidth } from '../components/configs';

const queryParamKey = 'update-todo';
const dateFormat = 'YYYY-MM-DD';

const generateDefaultState = () => {
	return {
		date: '',
		name: '',
		remarks: '',
	}
}

const useStyles = makeStyles(theme => ({
	container: {
		maxWidth,
		margin: 'auto',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 10,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	},
	deleteButton: {
		width: '100%',
		marginTop: 10,
		marginBottom: 20,
		borderRadius: 6,
		fontWeight: 600,
		color: theme.palette.error.main,
	},
	loading: {
		marginTop: 50,
		marginBottom: 50,
		color: '#c7c7c7'
	},
	dialogContentWrapper: {
		display: 'flex',
		justifyContent: 'space-evenly',
		marginTop: 10,
		marginBottom: 10,
	}
}));

function UpdateTodoDialog() {
	const classes = useStyles();
	// eslint-disable-next-line
	const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
	const [queryParams, setQueryParams] = useQueryParams();
	const [disabled, setDisabled] = useState(false);
	const [deleteDisabled, setDeleteDisabled] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
	const [error, setError] = useState({});
	const handleSnackbarChange = useSnackbarText();
	const { todoDetailUpdate, handleChange: handleMainContextChange } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState) => {
		_setState(prevState => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleDateChange = (type) => (event, value) => {
		setState({ [type]: value });
	};

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.name) {
			noError = false;
			error.name = 'Invalid value.';
		}

		if(!state.date) {
			noError = false;
			error.date = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
			return;
		} 

		setDisabled(true);
		const { data, success } = await todoes.updateTodo({
			todoID: todoDetailUpdate.todoID,
			name: state.name,
			date: new Date(state.date),
			remarks: state.remarks,
		});
		setDisabled(false);

		if(!success) {
			handleSnackbarChange({ text: data.message, variant: 'error' });
			return;
		} else {
			handleSnackbarChange({ text: 'To do list updated', variant: 'success' });
			handleManualFetch();
			setQueryParams({ [queryParamKey]: null });
		}
	};

	const handleDeleteTodo = async () => {
		setDeleteDisabled(true);
		setQueryParams({ 'delete-todo': null });

		const { success, data } = await todoes.deleteTodo({
			todoID: todoDetailUpdate.todoID,
		});
		setDeleteDisabled(false);

		if(!success) {
			handleSnackbarChange({ text: data.message, variant: 'error' });
			return;
		}

		handleSnackbarChange({ text: 'To do list deleted', variant: 'success' });
		handleManualFetch();
		setQueryParams({ [queryParamKey]: null });
		handleMainContextChange({ todoDetailUpdate: null });
	};

	const handleDeletePress = () => {
		setQueryParams({ 'delete-todo': 'true' });
	};

	useEffect(() => {
		_setState(generateDefaultState(diary.selectedDate));
		if(!queryValue) handleMainContextChange({ todoDetailUpdate: null });
	}, [queryValue, diary.selectedDate, handleMainContextChange]);

	useEffect(() => {
		if(!todoDetailUpdate) {
			handleMainContextChange({ todoDetailUpdate: null });
			return setQueryParams({ [queryParamKey]: null });
		}

		const todoID = queryValue;

		const getData = async () => {
			let { data, success } = await todoes.getTodo({ todoID });

			if(success) {
				setLoaded(true);
				const todoDetails = data.todo;
				setState({
					date: moment(todoDetails.date).format(dateFormat),
					name: todoDetails.name,
					remarks: todoDetails.remarks,
				});
			} else {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			}
		};

		if(todoID) getData();
	}, [queryValue, todoDetailUpdate, setQueryParams, setState, handleSnackbarChange, handleMainContextChange]);

	return (
		<FullScreenDialog 
			title="Update To Do List"
			queryParamKey={queryParamKey}
		>
			{!loaded && <div className={classes.loading}>loading</div>}
			{loaded && (
				<div className={classes.container}>
					<DatePicker 
						label="Date"
						value={state.date}
						onChange={handleDateChange('date')}
						error={Boolean(error.date)}
						helperText={error.date}
						autoApply
					/>

					<TextField 
						label="Title*"
						value={state.name}
						onChange={handleChange('name')}
						error={Boolean(error.name)}
						helperText={error.name}
					/>

					<TextField 
						label="Remarks"
						value={state.remarks}
						onChange={handleChange('remarks')}
						error={Boolean(error.remarks)}
						helperText={error.remarks}
					/>

					<Button 
						className={classes.submitButton}
						onClick={handleSubmit}
						disabled={disabled}
					>
						Update
					</Button>
					<Button 
						className={classes.deleteButton}
						onClick={handleDeletePress}
						disabled={deleteDisabled}
					>
						Delete
					</Button>
				</div>
			)}

			{/* Todo delete confirm */}
			<Dialog title="Delete this to do list?" queryParamKey="delete-todo">
				<div className={classes.dialogContentWrapper}>
					<Button 
						variant="contained" 
						color="primary" 
						onClick={handleDeleteTodo}
					>
						Delete
					</Button>
					<Button onClick={() => setQueryParams({ 'delete-todo': null })}>
						Cancel
					</Button>
				</div>
			</Dialog>
		</FullScreenDialog>
	);
}

export default UpdateTodoDialog;
